import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import compact from 'lodash/compact';
import concat from 'lodash/concat';
import dropRight from 'lodash/dropRight';
import filter from 'lodash/filter';
import findLast from 'lodash/findLast';
import flatMap from 'lodash/flatMap';
import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import map from 'lodash/map';
import reject from 'lodash/reject';
import size from 'lodash/size';
import trim from 'lodash/trim';
import { v4 as uuid } from 'uuid';
import {
	findPersonaByName,
	findReaction,
	getOrderedPersonas,
	getRandomPersonas
} from '../data/Personas';
import type { Persona, Personas } from '../data/Personas';
import {
	containsConcept,
	findConceptByName,
	getConceptScore,
	getConcepts
} from '../data/Concepts';
import type { CategoryKey, Concept, ConceptKey } from '../data/Concepts';
import {
	findNewActivatedCombinations,
	getBonusScore
} from '../data/Combinations';
import type { Combinations } from '../data/Combinations';
import { sumPersonaScores, sumScores } from '../data/Score';
import type { PersonaScores, Score, TotalScore } from '../data/Score';
import { cleanupPlayerName, isAllowedPlayerName } from '../data/PlayerName';
import defaultConfiguration from '../data/Configuration';
import type { RootState } from './Store';

/**
 * The state of a game playthrough round
 */
export interface Round {
	/**
	 * Personas that have been added in this round.
	 * The total amount of all personas available is the sum of all personas in this round and all previous rounds
	 */
	personas: Personas;

	/**
	 * The concept selected in this round.
	 * If not set, this round has not yet ended
	 */
	concept?: Concept;

	/**
	 * Score received in this round for the selected concept without combination bonus.
	 * The total score for this round is the sum of this score with {@link Round.personaScores} and the {@link Round.combinations} bonus
	 * @see {@link getTotalScore}
	 */
	conceptScore?: Score;

	/**
	 * Score received in this round per persona for the selected concept.
	 * The total score for this round is the sum of this score with {@link Round.conceptScore} and the {@link Round.combinations} bonus
	 * @see {@link getTotalScore}
	 */
	personaScores?: PersonaScores;

	/**
	 * Combinations fulfilled with this round
	 */
	combinations?: Combinations;
}

/**
 * The current state of the game playthrough
 */
export interface PlaythroughSliceState {
	/**
	 * A unique key of the playthrough
	 */
	key: string;

	/**
	 * Name of the player in this playthrough
	 */
	playerName: string;

	/**
	 * Number of rounds to play in this playthrough
	 */
	roundCount: number;

	/**
	 * All rounds played so far in the current playthrough including the current round.
	 * Each playthrough starts with an initial round, so the set of rounds can never be empty.
	 */
	rounds: Round[];

	/**
	 * The currently current selected category
	 */
	currentCategory: CategoryKey;
}

const getNewPersonas = (
	personaCount: number,
	randomPersonas: boolean,
	actualPersonas: Persona[] = []
): Persona[] => {
	return randomPersonas
		? getRandomPersonas(personaCount, actualPersonas)
		: getOrderedPersonas(personaCount, actualPersonas);
};

const createEmptyRound = (personas: Persona[]): Round => {
	return {
		personas,
		concept: undefined,
		conceptScore: undefined,
		personaScores: undefined,
		combinations: undefined
	};
};

const createInitialRound = (
	personaCount: number,
	randomPersonas: boolean
): Round => {
	return createEmptyRound(getNewPersonas(personaCount, randomPersonas));
};

const generateKey = (): string => uuid();

const createInitialPlaythrough = (
	playerName: string,
	roundCount: number,
	personaCount: number,
	randomPersonas: boolean,
	currentCategory: CategoryKey
): PlaythroughSliceState => {
	return {
		key: generateKey(),
		playerName,
		roundCount,
		rounds: [createInitialRound(personaCount, randomPersonas)],
		currentCategory
	};
};

/**
 * @returns The actual round in the current playthrough, regardless of whether it has already finished or not.
 *
 */
export const getCurrentRound = (rounds: Round[]): Round | undefined => {
	return last(rounds);
};

const isRoundFinished = (round: Round): boolean => {
	return !!round?.concept;
};

export const getLastFinishedRound = (rounds: Round[]): Round | undefined => {
	return findLast(rounds, isRoundFinished);
};

export const getFinishedRounds = (rounds: Round[]): Round[] => {
	return filter(rounds, isRoundFinished);
};

export const getFinishedRoundsCount = (rounds: Round[]): number => {
	return size(getFinishedRounds(rounds));
};

/**
 * @returns The rounds without the last one
 */
export const getPreviousRounds = (rounds: Round[]): Round[] => {
	return dropRight(rounds, 1);
};

/**
 * @returns The current round index as a numeric value between [0 ; {@link PlaythroughSliceState.roundCount}[
 *
 * Note: After the last round, the index does not increase because no new round is added after the last round is completed.
 */
export const getCurrentRoundIndex = (rounds: Round[]): number => {
	if (isEmpty(rounds)) {
		throw new RangeError('Rounds are empty');
	}
	return size(rounds) - 1;
};

const isLastRound = (state: PlaythroughSliceState): boolean => {
	const maxRoundIndex = state.roundCount - 1;
	return getCurrentRoundIndex(state.rounds) >= maxRoundIndex;
};

export const isPlaythroughFinished = (
	state: PlaythroughSliceState
): boolean => {
	if (isLastRound(state)) {
		const currentRound = getCurrentRound(state.rounds);
		return !!currentRound && isRoundFinished(currentRound);
	}
	return false;
};

/**
 * Returns all personas that are available
 */
export const getAllPersonas = (rounds: Round[]): Persona[] => {
	return flatMap(rounds, (round) => round.personas);
};

/**
 * Returns all concepts that have been activated
 */
export const getAllActiveConcepts = (rounds: Round[]): Concept[] => {
	return flatMap(rounds, (round) => round.concept ?? []);
};

/**
 * Returns all concepts that are not activated
 */
export const getAllInactiveConcepts = (rounds: Round[]): Concept[] => {
	const activeConcepts = getAllActiveConcepts(rounds);
	return reject(getConcepts(), (concept) =>
		containsConcept(activeConcepts, concept)
	);
};

const getPersonaHappiness = (
	personaName: string,
	conceptName: ConceptKey
): number => {
	const persona = findPersonaByName(personaName);
	if (persona) {
		const concept = findConceptByName(conceptName);
		if (concept) {
			const reaction = findReaction(persona, concept);
			return reaction?.points ?? 0;
		}
	}
	return 0;
};

/**
 * Calculates score for all personas in the last finished round of the current playthrough.
 * Scores from previous rounds are not included
 *
 * @param concept The concept chosen in the last round
 */
const calculatePersonaScoresForRound = (
	concept: Concept,
	state: PlaythroughSliceState
): PersonaScores => {
	const scores: PersonaScores = {};
	const personas = getAllPersonas(state.rounds);
	const personaCount = size(personas);
	for (let personaIndex = 0; personaIndex < personaCount; ++personaIndex) {
		const personaName = personas[personaIndex].name;
		scores[personaName] = {
			environment: undefined,
			happiness: getPersonaHappiness(personaName, concept.name),
			qualityOfLife: undefined
		};
	}
	return scores;
};

/**
 * @param concept The concept chosen in the last round
 */
const findCombinationsForRound = (
	concept: Concept,
	state: PlaythroughSliceState
): Combinations => {
	const activeConcepts = getAllActiveConcepts(state.rounds);
	return findNewActivatedCombinations(concept, activeConcepts);
};

export const getPersonaScores = (round?: Round): PersonaScores => {
	return round?.personaScores ?? {};
};

const getCombinationsScore = (round?: Round): Score => {
	return sumScores(map(round?.combinations, getBonusScore));
};

/**
 * Calculates total score from concepts and personas for all finished rounds in the current playthrough
 */
export const getTotalScore = (rounds: Round[]): TotalScore => {
	const allConceptScores = compact(map(rounds, (round) => round.conceptScore));
	const allPersonaScores = map(rounds, (round) =>
		sumPersonaScores(getPersonaScores(round))
	);
	const allCombinationScores = map(rounds, (round) =>
		getCombinationsScore(round)
	);
	const allScores = concat(
		allConceptScores,
		allPersonaScores,
		allCombinationScores
	);
	return sumScores(allScores);
};

export interface NewPlaythroughArgs {
	/**
	 * Name of the player in the new playthrough
	 */
	playerName?: string;
	/**
	 * Number of rounds in the new playthrough
	 */
	roundCount?: number;
	/**
	 * Persona count in the initial round
	 */
	initialPersonaCount?: number;
	/**
	 * Use random initial personas or not
	 */
	randomInitialPersonas?: boolean;
	/**
	 * Preselected category in the new playthrough
	 */
	currentCategory?: CategoryKey;
}

interface NewPlaythroughResult {
	playerName: string;
	roundCount: number;
	initialRound: Round;
	currentCategory: CategoryKey;
}

export const newPlaythrough = createAsyncThunk<
	NewPlaythroughResult,
	NewPlaythroughArgs,
	{ state: RootState }
>(
	'playthrough/newPlaythrough',
	async (arg: NewPlaythroughArgs, thunkApi): Promise<NewPlaythroughResult> => {
		const { configuration, playthrough: state } = thunkApi.getState();
		const {
			playerName = state.playerName ?? configuration.defaultPlayerName,
			initialPersonaCount = configuration.initialPersonaCountPerPlaythrough,
			randomInitialPersonas = configuration.randomInitialPersonas,
			roundCount = configuration.roundCountPerPlaythrough,
			currentCategory = state.currentCategory ?? configuration.defaultCategory
		} = arg;

		const cleanPlayerName = trim(cleanupPlayerName(playerName));

		return {
			playerName: isAllowedPlayerName(cleanPlayerName)
				? cleanPlayerName
				: configuration.defaultPlayerName,
			roundCount,
			initialRound: createInitialRound(
				initialPersonaCount,
				randomInitialPersonas
			),
			currentCategory
		};
	}
);

interface ResetPlaythroughArgs {
	/**
	 * Also resets the personas.
	 * If the configuration parameter 'randomInitialPersonas' is set to true,
	 * this changes the personas of the first round.
	 */
	resetPersonas: boolean;
}

type ResetPlaythroughResult = PlaythroughSliceState;

const resetPlaythrough = createAsyncThunk<
	ResetPlaythroughResult,
	ResetPlaythroughArgs,
	{ state: RootState }
>(
	'playthrough/resetPlaythrough',
	async (
		arg: ResetPlaythroughArgs,
		thunkApi
	): Promise<ResetPlaythroughResult> => {
		const { configuration, playthrough } = thunkApi.getState();
		const { resetPersonas } = arg;

		const initialRound: Round =
			resetPersonas || isEmpty(playthrough.rounds)
				? createInitialRound(
						configuration.initialPersonaCountPerPlaythrough,
						configuration.randomInitialPersonas
				  )
				: createEmptyRound(playthrough.rounds[0].personas);

		return {
			key: playthrough.key,
			playerName: playthrough.playerName,
			roundCount: playthrough.roundCount,
			rounds: [initialRound],
			currentCategory: playthrough.currentCategory
		};
	}
);

interface NextRoundArgs {
	/**
	 * Additional persona count in the next round
	 */
	additionalPersonaCount?: number;
	/**
	 * Use random additional personas or not
	 */
	randomAdditionalPersonas?: boolean;
	/**
	 * Selected concept in the current round
	 */
	concept: Concept;
}

interface NextRoundResult {
	concept: Concept;
	conceptScore: Score;
	personaScores: PersonaScores;
	combinations: Combinations;
	nextRound?: Round;
}

export const nextRound = createAsyncThunk<
	NextRoundResult,
	NextRoundArgs,
	{ state: RootState }
>(
	'playthrough/nextRound',
	async (arg: NextRoundArgs, thunkApi): Promise<NextRoundResult> => {
		const { configuration, playthrough: state } = thunkApi.getState();
		const {
			concept,
			additionalPersonaCount = configuration.additionalPersonaCountPerRound,
			randomAdditionalPersonas = configuration.randomAdditionalPersonas
		} = arg;

		if (isPlaythroughFinished(state)) {
			throw new Error('Playthrough has already finished');
		}
		const currentRound = getCurrentRound(state.rounds);
		if (!currentRound || isRoundFinished(currentRound)) {
			throw new Error('Round has already finished');
		}

		// #region Calculate scores for the round
		const conceptScore = getConceptScore(concept);
		const personaScores = calculatePersonaScoresForRound(concept, state);
		const combinations = findCombinationsForRound(concept, state);
		// #endregion

		// #region Add new round
		let nextRound: Round | undefined = undefined;
		if (!isLastRound(state)) {
			nextRound = {
				personas: getNewPersonas(
					additionalPersonaCount,
					randomAdditionalPersonas,
					getAllPersonas(state.rounds)
				),
				concept: undefined,
				conceptScore: undefined,
				personaScores: undefined,
				combinations: undefined
			};
		}
		// #endregion

		return {
			concept,
			conceptScore,
			personaScores,
			combinations,
			nextRound
		};
	}
);

export const finishIntro = createAsyncThunk<void, void, { state: RootState }>(
	'intro/finish',
	async (_: void, thunkApi): Promise<void> => {
		const { configuration } = thunkApi.getState();
		const { resetPlaythroughAfterIntro } = configuration;

		if (resetPlaythroughAfterIntro) {
			await thunkApi.dispatch(resetPlaythrough({ resetPersonas: false }));
		}
	}
);

const playthroughSlice = createSlice({
	name: 'playthrough',
	initialState: createInitialPlaythrough(
		defaultConfiguration.defaultPlayerName,
		defaultConfiguration.roundCountPerPlaythrough,
		defaultConfiguration.initialPersonaCountPerPlaythrough,
		defaultConfiguration.randomInitialPersonas,
		defaultConfiguration.defaultCategory
	),
	reducers: {
		setCurrentCategory: (state, action: PayloadAction<CategoryKey>): void => {
			const category = action.payload;
			state.currentCategory = category;
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(newPlaythrough.fulfilled, (state, action) => {
				const { playerName, roundCount, initialRound, currentCategory } =
					action.payload;

				state.key = generateKey();
				state.playerName = playerName;
				state.roundCount = roundCount;
				state.rounds = [initialRound];
				state.currentCategory = currentCategory;
			})
			.addCase(resetPlaythrough.fulfilled, (_, action) => {
				const newState = action.payload;
				return newState;
			})
			.addCase(nextRound.fulfilled, (state, action) => {
				const {
					concept,
					conceptScore,
					personaScores,
					combinations,
					nextRound
				} = action.payload;

				const currentRound = getCurrentRound(state.rounds);
				if (!currentRound) {
					throw new Error('Current playthrough has no rounds');
				}

				currentRound.concept = concept;
				currentRound.conceptScore = conceptScore;
				currentRound.personaScores = personaScores;
				currentRound.combinations = combinations;
				if (nextRound) {
					state.rounds.push(nextRound);
				}
			});
	}
});

export const { setCurrentCategory } = playthroughSlice.actions;
export default playthroughSlice.reducer;
